/**
 * docFileNameDisplay — EDX-7 shared helper.
 *
 * Strips the folder portion from a `SiteDocument.fileName` so list +
 * card surfaces can render the leaf as the primary label and the path
 * as a small breadcrumb chip. Today uploads land with the full
 * relative path in `fileName` (e.g.
 * `Site Control/POA/z. Archive/foo.docx`); the leaf is everything
 * after the final `/`.
 *
 * Pure, deterministic, allocation-free for the common case (no `/`).
 * Two helpers because some surfaces want the breadcrumb segments
 * separately (so they can render `Site Control / POA / z. Archive`
 * with ChevronRight separators) and others just want a single
 * pre-joined string.
 *
 * `doc.folder` (a structured field set by the upload pipeline when a
 * folder is selected) is preferred over the path inferred from
 * `fileName` because the upload-folder field is what the user
 * actually picked. If `folder` is empty, we fall back to the
 * path-derived breadcrumb.
 */

export interface FileNameDisplay {
  /** Leaf — `fileName.split('/').pop() ?? fileName`. Always non-empty. */
  leaf: string;
  /** Folder breadcrumb segments. Empty when the file is at the root. */
  breadcrumb: string[];
}

/**
 * Split a path-style `fileName` into the leaf + the breadcrumb. The
 * caller is responsible for prefering the structured `doc.folder`
 * field when it's set — see `displayFileName` below for the doc-aware
 * variant that wraps this with the doc-field fallback.
 */
export function splitFilePath(fileName: string): FileNameDisplay {
  const safe = fileName ?? '';
  if (!safe.includes('/')) {
    return { leaf: safe, breadcrumb: [] };
  }
  const parts = safe.split('/').filter(p => p.length > 0);
  if (parts.length === 0) return { leaf: safe, breadcrumb: [] };
  const leaf = parts[parts.length - 1]!;
  const breadcrumb = parts.slice(0, -1);
  return { leaf, breadcrumb };
}

/**
 * Doc-aware variant — prefers the structured `folder` field over the
 * path-derived breadcrumb when both are present, since `folder`
 * reflects what the user explicitly picked at upload time.
 */
import { resolveSiteDocumentFileName } from './site/resolveSiteDocuments';

export function displayFileName(d: { fileName?: string; name?: unknown; id?: string; folder?: string | null }): FileNameDisplay {
  const folderField = (d.folder ?? '').trim();
  const split = splitFilePath(resolveSiteDocumentFileName({ fileName: d.fileName, name: d.name, id: d.id }, d.id));
  if (folderField) {
    // `folder` may carry slashes when nested. Use the structured field
    // verbatim — the upload pipeline already normalised it.
    const segs = folderField.split('/').filter(s => s.length > 0);
    return { leaf: split.leaf, breadcrumb: segs };
  }
  return split;
}

/** Join the breadcrumb with " / " for a compact single-line render. */
export function breadcrumbString(segs: string[]): string {
  return segs.join(' / ');
}
